import { computed, Injectable, signal } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import {
  CancelWaitingRoomDto,
  MagicLinkDto,
  PatientWaitingRoomInfoDto,
  QueueStatusDto,
  SubscriptionTokenDto,
  UpdatePatientDataDto,
  WaitingRoomPatientAccessService,
  WaitingRoomProviderInfoDto,
} from '@swagger-services/waiting-room';
import { NotificationSubscriberService } from './notification-subscriber.service';
import { environment } from 'src/environments/environment';
import { Capacitor } from '@capacitor/core';

@Injectable()
export class WaitingRoomService {
  private readonly _waitingRoom$ = new BehaviorSubject<PatientWaitingRoomInfoDto | undefined>(undefined);
  private readonly _provider$ = new BehaviorSubject<WaitingRoomProviderInfoDto | undefined>(undefined);
  private readonly _isEnteringMainPage$ = new BehaviorSubject<void>(undefined);
  private readonly _isLeavingMainPage$ = new BehaviorSubject<void>(undefined);
  private readonly _isLoading = signal<boolean>(false);

  readonly waitingRoom$: Observable<PatientWaitingRoomInfoDto | undefined> = this._waitingRoom$.pipe(
    filter((waitingRoom) => !!waitingRoom),
  );
  readonly provider$: Observable<WaitingRoomProviderInfoDto | undefined> = this._provider$.asObservable();
  readonly isEnteringMainPage$: Observable<void> = this._isEnteringMainPage$.asObservable();
  readonly isLeavingMainPage$: Observable<void> = this._isLeavingMainPage$.asObservable();
  readonly isLoading = computed(this._isLoading);

  constructor(
    private readonly notificationSubscriberService: NotificationSubscriberService,
    private readonly waitingRoomPatientAccessService: WaitingRoomPatientAccessService,
  ) {}

  get waitingRoom(): PatientWaitingRoomInfoDto {
    return this._waitingRoom$.value!;
  }

  setLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }

  clearState(): void {
    this._waitingRoom$.next(undefined);
    this._provider$.next(undefined);
    this.setLoading(false);
  }

  setIsEnteringMainPage(): void {
    this._isEnteringMainPage$.next();
  }

  setIsLeavingMainPage(): void {
    this._isLeavingMainPage$.next();
    this.clearState();
  }

  async refetchWaitingRoom(): Promise<PatientWaitingRoomInfoDto | undefined> {
    if (!this.waitingRoom) return undefined;
    return this.getWaitingRoomFromHash(this.waitingRoom.hash);
  }

  async getWaitingRoomFromHash(hash: string): Promise<PatientWaitingRoomInfoDto | undefined> {
    const res: PatientWaitingRoomInfoDto =
      await this.waitingRoomPatientAccessService.patientWaitingRoomInfoControllerFindOneByHash(environment.orgSlug, {
        hash,
      });
    this.setWaitingRoom(res);
    return res;
  }

  async getPositionInQueue(hash: string): Promise<QueueStatusDto> {
    return this.waitingRoomPatientAccessService.patientWaitingRoomInfoControllerGetPositionInQueue(
      environment.orgSlug,
      hash,
    );
  }

  async getVideoVisitLinkFromHash(hash: string): Promise<MagicLinkDto> {
    const res: MagicLinkDto =
      await this.waitingRoomPatientAccessService.patientWaitingRoomInfoControllerGetVideoMagicLink(
        environment.orgSlug,
        hash,
        Capacitor.isNativePlatform() ? undefined : encodeURI(`${window.location.origin}/waiting-room/${hash}`),
      );
    return res;
  }

  async refetchWebSocketToken(): Promise<SubscriptionTokenDto | undefined> {
    if (!this.waitingRoom) return undefined;
    return this.setWebSocketToken(this.waitingRoom);
  }

  async setWebSocketToken(waitingRoom: PatientWaitingRoomInfoDto): Promise<SubscriptionTokenDto | undefined> {
    const res = await this.waitingRoomPatientAccessService.patientWaitingRoomInfoControllerGetToken(
      environment.orgSlug,
      waitingRoom.id,
    );
    if (res) {
      this.notificationSubscriberService.setGroup(environment.orgSlug, waitingRoom.hash);
      this.notificationSubscriberService.setToken(res as SubscriptionTokenDto);
      this.notificationSubscriberService.subscribeToNotification();
    }
    return res;
  }

  async updatePatientData(id: string, data: UpdatePatientDataDto): Promise<PatientWaitingRoomInfoDto | undefined> {
    const res = await this.waitingRoomPatientAccessService.patientWaitingRoomInfoControllerUpdatePatientData(
      environment.orgSlug,
      id,
      data,
    );
    if (res) {
      this.setWaitingRoom({
        ...this.waitingRoom,
        ...res,
        provider: this._provider$!.value,
      });
    }
    return res;
  }

  async cancelVisit(cancelVisit: CancelWaitingRoomDto): Promise<PatientWaitingRoomInfoDto | undefined> {
    const res = await this.waitingRoomPatientAccessService.patientWaitingRoomInfoControllerCancel(
      environment.orgSlug,
      this.waitingRoom.id,
      cancelVisit,
    );
    if (res) {
      this.setWaitingRoom(res);
    }
    return res;
  }

  private setWaitingRoom(waitingRoom: PatientWaitingRoomInfoDto): void {
    if (waitingRoom.provider) {
      this.setProvider(waitingRoom.provider);
    }
    this._waitingRoom$.next(waitingRoom);
  }

  private setProvider(provider: WaitingRoomProviderInfoDto): void {
    this._provider$.next(provider);
  }
}
